import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

/**
 * Query-key prefixes to invalidate per mutation envelope `kind`. Extend this
 * map when a new copilot mutation tool ships with a left-panel view to sync.
 */
const MUTATION_QUERY_KEYS: Record<string, readonly (readonly string[])[]> = {
  priority_mutation: [
    ["priorities"],
    ["my-onething"],
    ["ppps"],
    ["team-priorities"],
    ["accountability"],
  ],
  ppp_mutation: [["ppps"], ["priorities"], ["my-onething"], ["team-priorities"], ["accountability"]],
  // PPP rows embed KR code/name, so KR/objective edits must refresh them too.
  objective_mutation: [["objectives"], ["key-results"], ["ppps"]],
  key_result_mutation: [["key-results"], ["objectives"], ["ppps"]],
  report_mutation: [["reports"], ["my-onething"]],
};

/**
 * Invalidates the left-panel TanStack Query caches affected by a successful
 * copilot mutation tool call. Call once from the component that mounts when
 * the mutation result envelope arrives (MutationResultCard) — the empty
 * dependency array relies on that component mounting fresh per tool call.
 */
export function useCopilotMutationSync(kind: string): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const keys = MUTATION_QUERY_KEYS[kind];
    if (!keys) return;
    for (const queryKey of keys) {
      queryClient.invalidateQueries({ queryKey: [...queryKey] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
